import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges } from '@angular/core';
import { RouterModule } from '@angular/router';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { KEYCLOAK_ROLES } from '../../../../shared/constants/auth.constants';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';
import { FinancialReconciliationSummary } from '../../../financial-reconciliation/models/financial-reconciliation.model';
import { FiscalOperationType, InvoiceStatus } from '../../../invoices/models/invoice.model';
import { DashboardChannel, DashboardKpis } from '../../models/dashboard.model';
import { costCovered, LOW_MARGIN_PERCENT, marginUnavailablePercent } from '../../utils/dashboard-actions';
import { currencyBRL, currencyCompact, delta, deltaClass, deltaIcon } from '../../utils/dashboard-format';

type QueryParams = Record<string, string | number | undefined>;

interface Variation {
  value: number;
  class: string;
  icon: string;
}

interface ContributionView {
  label: string;
  /** Texto no lugar do número quando a margem não é confiável. */
  unavailable: string | null;
  value: number;
  percent: string | null;
  danger: boolean;
  caption: string;
}

interface PayoutView {
  /** Falso quando o recorte não tem conferência de repasse: o valor some. */
  applies: boolean;
  caption: string;
  onlyMercadoLivre: boolean;
}

const pt = (n: number): string => n.toLocaleString('pt-BR', { maximumFractionDigits: 1 });

// Os 4 indicadores da home (spec §4): Faturamento, Pedidos, Contribuição e Repasse líquido. Cada card
// é um link para a lista JÁ no mesmo recorte (período e canal) que gerou o número, e só aparece para
// quem tem a permissão da lista. Faturamento vem da nota (`invoice`); os demais, do pedido (`order`).
//
// `loading`: o destino dos cards não depende do snapshot, então no primeiro load eles já navegam
// com o número em placeholder.
//
// A variação só aparece com período anterior maior que zero (`previous*` nulo ou 0 = sem base) e
// quando `noComparison` é falso (a empresa ou o canal começou dentro do período: aviso de período
// curto da home).
@Component({
  selector: 'app-dashboard-kpi-cards',
  standalone: true,
  imports: [CommonModule, RouterModule, NgbTooltipModule, HasPermissionDirective],
  // Host = a faixa: um card só, dividido por hairlines (ver .kpi-strip em styles.scss).
  host: { class: 'card kpi-strip kpi-cards mb-4' },
  templateUrl: './dashboard-kpi-cards.component.html',
  styleUrl: './dashboard-kpi-cards.component.scss',
})
export class DashboardKpiCardsComponent implements OnChanges {
  @Input() kpis?: DashboardKpis | null;
  /** Resumo da conciliação no período (já filtrado pelo canal escolhido). */
  @Input() summary?: FinancialReconciliationSummary | null;
  @Input() channels?: DashboardChannel[] | null;
  @Input() channelId: number | null = null;
  /** Período do painel (from/to yyyy-MM-dd): as listas abrem no mesmo recorte do número. */
  @Input() period?: { from?: string; to?: string };
  /** Primeiro load: números em placeholder, mas o card já navega. */
  @Input() loading = false;
  /** Empresa ou canal começou dentro do período: nenhum indicador mostra variação. */
  @Input() noComparison = false;

  readonly roles = KEYCLOAK_ROLES;
  readonly compact = currencyCompact;

  links = { invoices: {} as QueryParams, orders: {} as QueryParams, reconciliation: {} as QueryParams };
  revenueVariation: Variation | null = null;
  ordersVariation: Variation | null = null;
  revenueCaption = '';
  ordersCaption = '';
  revenueTip = '';
  contribution: ContributionView | null = null;
  payout: PayoutView | null = null;

  ngOnChanges(): void {
    this.links = this.buildLinks();
    const kpis = this.kpis;
    this.revenueVariation = this.variation(kpis?.revenue ?? 0, kpis?.previousRevenue);
    this.ordersVariation = this.variation(kpis?.orders ?? 0, kpis?.previousOrders);
    this.revenueTip = (kpis?.revenue ?? 0) >= 100_000 ? currencyBRL(kpis!.revenue) : '';
    this.revenueCaption = this.buildRevenueCaption();
    this.ordersCaption = this.buildOrdersCaption();
    this.contribution = this.buildContribution();
    this.payout = this.buildPayout();
  }

  private variation(current: number, previous: number | null | undefined): Variation | null {
    if (this.noComparison) return null;
    const value = delta(current, previous ?? 0);
    return value === null ? null : { value, class: deltaClass(value), icon: deltaIcon(value) };
  }

  /** Recorte comum das três listas: período e canal (só quando há canal escolhido). */
  private buildLinks(): DashboardKpiCardsComponent['links'] {
    const scope: QueryParams = {
      ...this.period,
      ...(this.channelId != null ? { integrationId: this.channelId } : {}),
    };
    return {
      // Mesmo recorte do faturamento: autorizada + venda, de qualquer origem. O "Valor total" da lista bate
      // com o card: as duas contam pela emissão (IssuedAt ?? CreatedAt) e a Autorizada exclui a cancelada.
      invoices: {
        ...scope,
        status: InvoiceStatus.Authorized,
        fiscalOperation: FiscalOperationType.Sale,
        includeFulfilled: 'true',
        includeErp: 'true',
      },
      // As grades escondem Full por padrão; o card conta (é venda).
      orders: { ...scope, includeFulfilled: 'true' },
      reconciliation: scope,
    };
  }

  private buildRevenueCaption(): string {
    const k = this.kpis;
    if (!k) return '';
    if (k.salesInvoices <= 0) return 'Nenhuma nota de venda no período';
    const notes = `${k.salesInvoices} ${k.salesInvoices === 1 ? 'nota de venda' : 'notas de venda'}`;
    return `${notes}, ticket ${currencyCompact(k.revenue / k.salesInvoices)}`;
  }

  private buildOrdersCaption(): string {
    const k = this.kpis;
    if (!k) return '';
    if (k.orders <= 0) return 'Nenhum pedido no período';
    const parts: string[] = [];
    if (k.ordersToInvoice > 0) parts.push(`${k.ordersToInvoice} a faturar`);
    if (k.ordersCancelled > 0) {
      const share = pt((k.ordersCancelled / k.orders) * 100);
      parts.push(`${k.ordersCancelled} ${k.ordersCancelled === 1 ? 'cancelado' : 'cancelados'} (${share}%)`);
    }
    return parts.length ? parts.join(', ') : 'Nenhum a faturar ou cancelado';
  }

  // Contribuição só com custo em 80% ou mais das vendas; senão "Antes do custo" (mostrar valor limpo
  // com custo faltando seria uma margem que não existe). Margem indisponível vale antes de tudo.
  private buildContribution(): ContributionView | null {
    const s = this.summary;
    if (!s) return null;

    const unlinked = marginUnavailablePercent(s, this.kpis?.revenue);
    if (unlinked !== null) {
      return {
        label: 'Contribuição',
        unavailable: `Margem indisponível: ${unlinked}% das notas sem pedido vinculado`,
        value: 0,
        percent: null,
        danger: false,
        caption: '',
      };
    }

    const coverage = s.costCoveragePercent ?? 0;
    const costed = costCovered(s);
    const value = costed ? s.costedMargin.cleanValue : (s.margin?.preCostResult ?? 0);
    const pct = costed ? s.costedMargin.cleanValuePercent : s.margin?.preCostPercent;
    const hasPct = pct !== null && pct !== undefined;
    return {
      label: costed ? 'Contribuição' : 'Antes do custo',
      unavailable: null,
      value,
      percent: hasPct ? pt(pct) : null,
      // Abaixo de 5% é margem baixa da contribuição; antes do custo só fica vermelho se for negativo.
      danger: value < 0 || (costed && hasPct && pct < LOW_MARGIN_PERCENT),
      caption: coverage > 0 ? `Custo informado em ${pt(coverage)}% das vendas` : 'Custo das peças não informado',
    };
  }

  private buildPayout(): PayoutView | null {
    const s = this.summary;
    if (!s) return null;

    const channels = this.channels ?? [];
    const chosen = this.channelId != null ? channels.find((c) => c.integrationId === this.channelId) : undefined;
    if (chosen && !chosen.hasPayoutReconciliation) {
      return { applies: false, caption: 'Este canal não tem conferência de repasse', onlyMercadoLivre: false };
    }
    const withPayout = channels.filter((c) => c.hasPayoutReconciliation).length;
    if (channels.length > 0 && withPayout === 0) {
      return { applies: false, caption: 'Nenhum canal tem conferência de repasse', onlyMercadoLivre: false };
    }

    const caption =
      s.paymentsGross > 0
        ? `${pt((s.paymentsNet / s.paymentsGross) * 100)}% do valor pago pelos clientes`
        : 'Nenhum pagamento no período';
    // Em "Todos os canais", outros canais sem conferência de repasse ficam fora do número.
    const onlyMercadoLivre = chosen === undefined && withPayout < channels.length;
    return { applies: true, caption, onlyMercadoLivre };
  }
}
